import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBuilding, FaChartBar, FaPlus } from 'react-icons/fa';
import Header from '../Header';
import api from '../services/apiService';
import { authService } from '../services/authService';
import { academyTreasurerPathFor, getCachedAcademies, personDisplayName, personHasKnownRole, personRoles, setCachedAcademies, ACADEMY_ADMIN_API } from '../utils/academyAccess';
import { loadAcademyDirectory } from '../utils/academyTeamSort';
import AcademyPersonSearch from './AcademyPersonSearch';
import AcademyTeamPicker from './AcademyTeamPicker';
import '../scss/admin/AcademySubscriptions.scss';

const pounds = (pence) => (Number(pence || 0) / 100).toFixed(2);

const AcademySubscriptions = () => {
  const navigate = useNavigate();
  const isSuperAdmin = authService.isSuperAdmin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [people, setPeople] = useState([]);
  const [creating, setCreating] = useState(false);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [treasurerPath, setTreasurerPath] = useState(() => (
    academyTreasurerPathFor(getCachedAcademies(), null, isSuperAdmin)
  ));

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_academy_subscriptions.php`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not load academies');
      }
      const academies = response.data.academies || [];
      setRows(academies);
      if (!isSuperAdmin) {
        setCachedAcademies(academies);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load academies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadAccess = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        const list = response.data?.success ? (response.data.academies || []) : [];
        if (!cancelled) {
          setTreasurerPath(academyTreasurerPathFor(list, null, isSuperAdmin));
        }
      } catch (err) {
        if (!cancelled && !isSuperAdmin) {
          setTreasurerPath(null);
        }
      }
    };
    loadAccess();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    load();
    if (!isSuperAdmin) {
      return;
    }
    const loadTeams = async () => {
      try {
        setAllTeams(await loadAcademyDirectory(api));
      } catch (err) {
        setAllTeams([]);
      }
    };
    loadTeams();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Enter an academy name');
      return;
    }
    if (!people.length) {
      setError('Assign at least one person');
      return;
    }
    if (people.some((row) => !personHasKnownRole(row))) {
      setError('Choose at least one role for each person');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/save_academy_subscription.php`, {
        name: name.trim(),
        amount_pence: 0,
        season_start_month: 8,
        season_end_month: 7,
        enabled: true,
        people: people.map((row) => ({
          person_type: row.person_type,
          person_id: row.person_id,
          roles: personRoles(row),
          role: personRoles(row)[0] || '',
        })),
        team_ids: selectedTeamIds,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not create academy');
      }
      navigate(`/academy-subscriptions/${response.data.academy.id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not create academy');
    } finally {
      setCreating(false);
    }
  };

  const assignedLabel = (row) => {
    const admins = row.admins || [];
    if (admins.length === 0) {
      return 'No one assigned';
    }
    return admins.map((admin) => {
      const name = personDisplayName(admin);
      const roles = personRoles(admin);
      return roles.length ? `${name} (${roles.join(', ')})` : name;
    }).join(', ');
  };

  return (
    <div className="academy-subscriptions container">
      <Header />
      <div className="page-header">
        <h1>
          <FaBuilding /> {isSuperAdmin ? 'Create an Academy' : 'Academy settings'}
        </h1>
        {treasurerPath ? (
          <Link to={treasurerPath} className="header-link">
            <FaChartBar aria-hidden="true" /> Treasurer Reports
          </Link>
        ) : null}
      </div>
      <p className="page-intro">
        {isSuperAdmin
          ? 'Create an academy, assign people and roles, and pick existing teams. Academy Admins edit settings. Club Secretaries see parent payments. Treasurers open Treasurer Reports.'
          : 'Monthly club fees, Stripe, season months, and which First Whistle teams belong to your academy.'}
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {isSuperAdmin && (
        <form className="create-box" onSubmit={handleCreate}>
          <label className="field">
            New academy
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Academy United"
            />
          </label>
          <div className="field">
            <span>Assign people</span>
            <AcademyPersonSearch people={people} onChange={setPeople} />
          </div>
          <div className="field">
            <span>Assign existing teams</span>
            <AcademyTeamPicker
              teams={allTeams}
              selectedTeamIds={selectedTeamIds}
              onToggle={(teamId) => {
                setSelectedTeamIds((current) => (
                  current.includes(teamId)
                    ? current.filter((value) => value !== teamId)
                    : [...current, teamId]
                ));
              }}
              hint="Existing First Whistle teams only. You can add more later."
            />
          </div>
          <button className="save-button" type="submit" disabled={creating}>
            <FaPlus /> {creating ? 'Creating…' : 'Create an Academy'}
          </button>
        </form>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        rows.map((row) => (
          <div
            key={row.id}
            className="academy-card"
            onClick={() => navigate(`/academy-subscriptions/${row.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(`/academy-subscriptions/${row.id}`);
              }
            }}
          >
            <div>
              <h2>{row.name}</h2>
              <p>
                £{pounds(row.amount_pence)} / month · {row.team_count} team{row.team_count === 1 ? '' : 's'} · {row.season_label}
              </p>
              <p>{assignedLabel(row)}</p>
            </div>
            <span className="status">
              {row.stripe_connected ? 'Stripe connected' : 'Stripe not connected'}
              {row.stripe_connected && row.team_count > 0 ? ' · Live' : ''}
            </span>
          </div>
        ))
      )}
    </div>
  );
};

export default AcademySubscriptions;
